import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatEUR } from "../lib/format";
import { AlertTriangle, ChevronDown, ChevronRight, Trash2, RefreshCw, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const MESES = ["","Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

export default function Duplicados() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // group_id -> bool
  const [busy, setBusy] = useState({});
  const [cleaningLegacy, setCleaningLegacy] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [purgingFixed, setPurgingFixed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/duplicados?year=${year}`),
        api.get(`/duplicados/legacy-fixed-check?year=${year}`).catch(() => ({data: null})),
      ]);
      setData(r1.data);
      setLegacy(r2.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando duplicados");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const deleteEntry = async (entry_id, concepto) => {
    if (!window.confirm(`¿Borrar la entrada "${concepto}"? Esta acción es permanente. Ninguna otra copia del grupo se toca.`)) return;
    setBusy(b => ({ ...b, [entry_id]: true }));
    try {
      await api.delete(`/cashflow/entries/${entry_id}`);
      toast.success("Entrada borrada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error borrando");
    } finally {
      setBusy(b => ({ ...b, [entry_id]: false }));
    }
  };

  const cleanLegacy = async () => {
    if (!window.confirm(`¿Borrar los ${legacy.legacy_fixed.count} gastos fijos ANTIGUOS (source='fixed') del ${year}?\n\nImporte total: ${legacy.legacy_fixed.total_euros.toLocaleString('es-ES')} €\n\nLos gastos fijos DETALLADOS del nuevo sync (${legacy.new_fixed_proyectos.count} entradas por ${legacy.new_fixed_proyectos.total_euros.toLocaleString('es-ES')} €) se mantienen.\n\nEsta acción es IRREVERSIBLE.`)) return;
    setCleaningLegacy(true);
    try {
      const r = await api.post(`/duplicados/legacy-fixed-clean?year=${year}`);
      toast.success(`Limpieza: ${r.data.deleted} entries borradas, ${r.data.amount_freed_euros.toLocaleString('es-ES')} € liberados del cashflow.`, { duration: 8000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error limpiando");
    } finally { setCleaningLegacy(false); }
  };

  const purgeFixedProyectos = async () => {
    if (!window.confirm(`¿Purgar TODOS los gastos fijos que llegaron por sync automático desde Proyectos?\n\nEsto borra:\n- entries con source='fixed_proyectos' o project_code='GASTO_FIJO'\n- definiciones en 'fixed_expenses' con source='proyectos'\n\nA partir de ahora los gastos fijos se subirán manualmente en Cashflow.\nEsta acción es IRREVERSIBLE.`)) return;
    setPurgingFixed(true);
    try {
      const r = await api.post(`/cashflow/purge-fixed-proyectos`);
      toast.success(`Purga completada: ${r.data.en_deleted} entries y ${r.data.fx_deleted} definiciones borradas.`, { duration: 8000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error purgando");
    } finally { setPurgingFixed(false); }
  };

  const normalizeCodes = async () => {
    if (!window.confirm(`Normalizar códigos de proyecto de ${year} con ceros a la izquierda.\n\nEjemplo: '0536' → '536' (unificar con lo que trae el API).\n\nSi al normalizar hay entries idénticas duplicadas, se conserva la canónica sin cero. Esta acción es IRREVERSIBLE.`)) return;
    setNormalizing(true);
    try {
      const r = await api.post(`/normalize-project-codes?year=${year}`);
      const d = r.data;
      if (d.unique_legacy_codes_found === 0) {
        toast.info("No se encontraron códigos con ceros a la izquierda. Nada que hacer.", { duration: 6000 });
      } else {
        toast.success(`Normalizados ${d.unique_legacy_codes_found} códigos (${d.codes_normalized.join(", ")}). Renombrados: ${d.renamed}. Duplicados borrados: ${d.duplicates_removed}.`, { duration: 10000 });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error normalizando");
    } finally { setNormalizing(false); }
  };

  const groups = data?.groups || [];
  const altas = groups.filter(g => g.severity === "alta");
  const medias = groups.filter(g => g.severity === "media");

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="duplicados-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Duplicados sospechosos</h1>
          <p className="text-[var(--ink-muted)] text-sm mt-1 max-w-2xl">
            Detector de entries repetidas por proyecto + importe + concepto. <strong>Ninguna acción automática</strong> — cada duplicado lo revisas y borras a mano. Los gastos fijos y nóminas recurrentes están excluidos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={e=>setYear(parseInt(e.target.value))} className="border rounded px-2 py-1.5 text-sm" data-testid="dup-year">
            {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
          </select>
          <button onClick={load} className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-[var(--bg)]" data-testid="dup-reload">
            <RefreshCw size={14}/> Recargar
          </button>
        </div>
      </div>

      {/* Herramienta: purgar gastos fijos automáticos de Proyectos */}
      <div className="bg-orange-50 border border-orange-300 rounded-lg p-4" data-testid="purge-fixed-card">
        <div className="flex items-start gap-3">
          <Trash2 size={20} className="text-orange-600 shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-display text-base font-bold text-orange-900">Modo manual de gastos fijos</div>
            <p className="text-sm text-orange-800 mt-1">
              El sync automático de gastos fijos (nóminas, IVA, IRPF, préstamos, estructura) desde Proyectos está <strong>desactivado</strong>. Cuando quieras dejar el sistema en modo manual, borra los gastos fijos que ya trajo el sync antiguo con este botón. Los gastos fijos que subas <em>manualmente</em> desde Cashflow no se tocan.
            </p>
            <button onClick={purgeFixedProyectos} disabled={purgingFixed} className="mt-3 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm px-4 py-2 rounded font-semibold disabled:opacity-40" data-testid="purge-fixed-btn">
              {purgingFixed ? <RefreshCw size={14} className="animate-spin"/> : <Trash2 size={14}/>}
              Purgar gastos fijos automáticos (source=fixed_proyectos)
            </button>
          </div>
        </div>
      </div>

      {/* Herramienta: normalizar códigos con cero delante */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="normalize-codes-card">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-display text-base font-bold text-amber-900">Códigos de proyecto con ceros a la izquierda</div>
            <p className="text-sm text-amber-800 mt-1">
              A veces las entries antiguas del Excel usan <code className="bg-white/70 px-1 rounded font-mono-num">0536</code> mientras que el API trae <code className="bg-white/70 px-1 rounded font-mono-num">536</code>. Como son el MISMO proyecto pero con códigos distintos, se muestran como dos filas separadas en el cashflow. Este botón unifica los códigos: renombra los <code>0XXX</code> a <code>XXX</code> y borra los duplicados exactos que resulten.
            </p>
            <button onClick={normalizeCodes} disabled={normalizing} className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm px-4 py-2 rounded font-semibold disabled:opacity-40" data-testid="normalize-codes-btn">
              {normalizing ? <RefreshCw size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
              Normalizar códigos de {year}
            </button>
          </div>
        </div>
      </div>

      {/* Alerta legacy fixed */}
      {legacy?.is_duplicated && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5 shadow-sm" data-testid="legacy-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="text-red-600 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-display text-lg font-bold text-red-900">🚨 Gastos fijos DUPLICADOS entre importador antiguo y sync nuevo</div>
              <p className="text-sm text-red-800 mt-2 leading-relaxed">
                Detectado que en {year} hay <strong>{legacy.legacy_fixed.count} gastos con <code className="bg-white/60 px-1 rounded">source=fixed</code></strong> (importador Excel antiguo, agregado por bloques) que suman <strong>{legacy.legacy_fixed.total_euros.toLocaleString('es-ES')} €</strong>, coexistiendo con <strong>{legacy.new_fixed_proyectos.count} gastos <code className="bg-white/60 px-1 rounded">source=fixed_proyectos</code></strong> (nuevo sync, desagregados por concepto y mes) que suman <strong>{legacy.new_fixed_proyectos.total_euros.toLocaleString('es-ES')} €</strong>.
              </p>
              <p className="text-sm text-red-800 mt-2">
                <strong>Consecuencia:</strong> el cashflow está mostrando ~{legacy.legacy_fixed.total_euros.toLocaleString('es-ES')} € de gastos duplicados (nóminas + IRPF + IVA + estructura + préstamos contados dos veces).
              </p>
              <div className="mt-4 flex items-center gap-3">
                <button onClick={cleanLegacy} disabled={cleaningLegacy} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded font-semibold disabled:opacity-40" data-testid="legacy-clean-btn">
                  {cleaningLegacy ? <RefreshCw size={14} className="animate-spin"/> : <Trash2 size={14}/>}
                  Borrar los {legacy.legacy_fixed.count} gastos antiguos ({legacy.legacy_fixed.total_euros.toLocaleString('es-ES')} €)
                </button>
                <span className="text-xs text-red-700">Los detallados del sync se conservan (más útiles para BI)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resumen */}
      {loading ? (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-[var(--ink-muted)]">Analizando entries…</div>
      ) : !data || data.count === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-8 text-center" data-testid="dup-clean">
          <ShieldCheck size={32} className="mx-auto text-emerald-600 mb-2"/>
          <div className="font-display text-xl font-bold text-emerald-800">Sin duplicados sospechosos</div>
          <div className="text-sm text-emerald-700 mt-1">No se han encontrado entries repetidas en {year}. Todo limpio.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Grupos sospechosos" value={data.count} color="rose" testid="dup-stat-count"/>
            <StatCard label="Severidad alta (real+previsto)" value={data.severity_counts.alta} color="red" testid="dup-stat-alta"/>
            <StatCard label="Severidad media" value={data.severity_counts.media} color="amber"/>
            <StatCard label="Impacto potencial €" value={formatEUR(data.impact_total_euros)} color="ink" testid="dup-stat-impact"/>
          </div>

          {altas.length > 0 && (
            <Section title="⚠️ Severidad alta — misma factura registrada como 'real' Y como 'previsto'" desc="Estos son duplicados casi seguros. Borra la versión 'previsto' (la real es la que se cobró/pagó de verdad)." groups={altas} expanded={expanded} setExpanded={setExpanded} deleteEntry={deleteEntry} busy={busy}/>
          )}
          {medias.length > 0 && (
            <Section title="Severidad media — mismo importe + concepto en fechas próximas" desc="Puede ser un duplicado real o un cobro/gasto legítimo repetido (p. ej. dos pagos parciales). Revisa contexto antes de borrar." groups={medias} expanded={expanded} setExpanded={setExpanded} deleteEntry={deleteEntry} busy={busy}/>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  const colorMap = {
    red: "text-red-700 border-red-200 bg-red-50",
    rose: "text-rose-700 border-rose-200 bg-rose-50",
    amber: "text-amber-700 border-amber-200 bg-amber-50",
    ink: "text-[var(--ink)] border-[var(--border)] bg-white",
  };
  return (
    <div className={`border rounded-lg p-4 ${colorMap[color]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-mono-num">{label}</div>
      <div className="mt-1 font-mono-num text-2xl font-bold">{value}</div>
    </div>
  );
}

function Section({ title, desc, groups, expanded, setExpanded, deleteEntry, busy }) {
  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <div className="border-b px-4 py-3">
        <div className="font-display text-lg font-bold flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600"/> {title}</div>
        <p className="text-xs text-[var(--ink-muted)] mt-0.5">{desc}</p>
      </div>
      <div className="divide-y">
        {groups.map(g => (
          <GroupRow key={g.group_id} g={g} isOpen={!!expanded[g.group_id]} onToggle={()=>setExpanded(e => ({...e, [g.group_id]: !e[g.group_id]}))} deleteEntry={deleteEntry} busy={busy}/>
        ))}
      </div>
    </div>
  );
}

function GroupRow({ g, isOpen, onToggle, deleteEntry, busy }) {
  const tinct = g.type === "Ingreso" ? "text-emerald-700" : "text-rose-700";
  return (
    <div>
      <button onClick={onToggle} className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center text-left hover:bg-[var(--hover)] text-sm" data-testid={`dup-group-${g.group_id}`}>
        <div className="col-span-1">{isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</div>
        <div className="col-span-2 font-mono-num text-xs text-[var(--ink-muted)]">{g.project_code}</div>
        <div className="col-span-4">
          <div className="text-sm">{g.concepto_sample}</div>
          <div className="text-[10px] text-[var(--ink-muted)]">{g.project_name}</div>
        </div>
        <div className={`col-span-2 font-mono-num text-right ${tinct}`}>{g.type === "Ingreso" ? "+" : "−"}{formatEUR(g.amount)}</div>
        <div className="col-span-1 text-center font-mono-num text-xs">×{g.count}</div>
        <div className="col-span-2 text-right font-mono-num text-xs">
          <div className="font-semibold text-rose-700">Impacto {formatEUR(g.impact_euros)}</div>
          <div className="text-[10px] opacity-70">{g.statuses.join(" + ")}</div>
        </div>
      </button>
      {isOpen && (
        <div className="bg-[var(--bg)] px-4 py-3 border-t">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[var(--ink-muted)] font-mono-num">
              <tr>
                <th className="text-left px-2 py-1">Concepto original</th>
                <th className="text-left px-2 py-1">Proveedor</th>
                <th className="text-center px-2 py-1">Quincena</th>
                <th className="text-center px-2 py-1">Status</th>
                <th className="text-right px-2 py-1">Importe</th>
                <th className="text-left px-2 py-1">Ref Proyectos</th>
                <th className="text-right px-2 py-1">Acción</th>
              </tr>
            </thead>
            <tbody>
              {g.entries.map(e => (
                <tr key={e.id} className="border-t bg-white">
                  <td className="px-2 py-1.5">{e.concepto || "—"}</td>
                  <td className="px-2 py-1.5 text-[var(--ink-muted)]">{e.proveedor || "—"}</td>
                  <td className="px-2 py-1.5 text-center font-mono-num">{MESES[e.month]} Q{e.quincena}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-block text-[10px] font-mono-num uppercase px-1.5 py-0.5 rounded ${e.proy_status==='real'?'bg-emerald-100 text-emerald-800':e.proy_status==='previsto'?'bg-amber-100 text-amber-800':'bg-slate-100 text-slate-700'}`}>
                      {e.proy_status || "—"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono-num">{formatEUR(e.amount)}</td>
                  <td className="px-2 py-1.5 text-[10px] font-mono-num text-[var(--ink-muted)]">{e.proy_ref_id || "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      onClick={() => deleteEntry(e.id, e.concepto || "sin concepto")}
                      disabled={busy[e.id]}
                      className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 text-xs disabled:opacity-40"
                      data-testid={`dup-delete-${e.id}`}
                    >
                      <Trash2 size={12}/> Borrar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-[var(--ink-muted)] mt-2 flex items-center gap-1">
            <ExternalLink size={10}/> Si borras aquí, sólo desaparece de Cashflow. La entry seguirá existiendo en Proyectos y volverá a aparecer en el siguiente sync (si vuelve).
          </p>
        </div>
      )}
    </div>
  );
}
